import { InteractionStatus } from '@azure/msal-browser'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/features/auth/hooks/useAuth'
import LoadingSpinner from '@/shared/components/ui/LoadingSpinner'

export default function LoginPage() {
  const { inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const { login } = useAuth()
  const location = useLocation()

  const from = (location.state as { from?: Location })?.from?.pathname ?? '/'

  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to={from} replace />
  }

  return (
    <div className="flex min-h-screen bg-surface">
      {/* Left panel — animated gradient background */}
      <div className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center relative overflow-hidden p-12"
           style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #7f1d1d 100%)' }}>

        {/* Animated floating orbs (keyframes in index.css) */}
        <div className="orb1 absolute w-96 h-96 rounded-full opacity-20 top-[-80px] left-[-80px]"
             style={{ background: 'radial-gradient(circle, #bf1b1b, transparent)' }} />
        <div className="orb2 absolute w-80 h-80 rounded-full opacity-15 bottom-[-60px] right-[-60px]"
             style={{ background: 'radial-gradient(circle, #3b82f6, transparent)' }} />
        <div className="orb3 absolute w-64 h-64 rounded-full opacity-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
             style={{ background: 'radial-gradient(circle, #bf1b1b, transparent)' }} />

        {/* Content over gradient */}
        <div className="relative z-10 flex flex-col items-center">
          <img src="/logo-hc_global_02.png" alt="HC Global" className="h-14 mb-8"
               onError={(e) => { e.currentTarget.style.display = 'none' }} />
          <h1 className="text-4xl font-extralight text-white text-center leading-snug drop-shadow">
            Billing{' '}
            <span className="font-semibold text-red-400">Portal</span>
          </h1>
          <p className="mt-4 text-blue-100/70 text-sm font-light text-center max-w-xs">
            Secure access to BOS client mapping, billing schedules and revenue forecasting.
          </p>
        </div>
      </div>

      {/* Right panel — sign-in */}
      <div className="flex flex-1 items-center justify-center px-8">
        <div className="w-full max-w-sm">
          {/* Mobile branding */}
          <div className="lg:hidden text-center mb-10">
            <img
              src="/logo-hc_global_02.png"
              alt="HC Global"
              className="h-10 mx-auto mb-4"
              onError={(e) => { e.currentTarget.style.display = 'none' }}
            />
            <h1 className="text-2xl font-extralight text-gray-800">
              Billing <span className="font-semibold text-primary">Portal</span>
            </h1>
          </div>

          <h2 className="text-lg font-semibold text-gray-800 mb-1">Sign in</h2>
          <p className="text-sm text-gray-500 mb-8">Use your HC Global Microsoft account</p>

          <button
            onClick={login}
            className="w-full flex items-center justify-center gap-3 rounded border border-gray-300
                       bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm
                       hover:bg-gray-50 hover:border-gray-400 transition-colors"
          >
            {/* Microsoft logo */}
            <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true" className="shrink-0">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            Sign in with Microsoft
          </button>

          <p className="mt-8 text-xs text-gray-400 text-center">
            HC Global Fund Services · Authorised users only
          </p>
        </div>
      </div>
    </div>
  )
}
